import { Card } from "@/components/ui/card";
import { Heading } from "@/components/heading";
import { environment } from "@/relay/environment";
import {
	createFileRoute,
	useLoaderData,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import {
	ConnectionHandler,
	fetchQuery,
	graphql,
	useMutation,
} from "react-relay";
import { z } from "zod";
import type { postsCreateQuery } from "./__generated__/postsCreateQuery.graphql";
import { useEditor } from "@/editor/editor";
import { EditorContent } from "@tiptap/react";
import type { postsCreateMutation } from "./__generated__/postsCreateMutation.graphql";
import { useState } from "react";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { toast } from "sonner";
import { PostForm, type PostFormSchemaValues } from "@/forms/post-form";

export const Route = createFileRoute("/_visibility/_topnav/posts/novo")({
	component: RouteComponent,
	validateSearch: z.object({
		space: z.string().optional(),
	}),
	loaderDeps: ({ search }) => [search.space],
	loader: async ({ context: { club }, deps: [space] }) => {
		const data = await fetchQuery<postsCreateQuery>(
			environment,
			graphql`
	    query postsCreateQuery($club: String!) {
			club(slug: $club) {
			  groups {
					spaces {
					  id
						name
						slug
						icon
						isMember
						visibility
						postPolicy
					}
			  }
				rules {
					title
					description
				}
			}
		}
	`,
			{ club: club.slug },
		).toPromise();

		const groups = data?.club?.groups || [];
		const allSpaces = groups.filter(Boolean).flatMap((g) => g.spaces);
		const availableSpaces =
			club.permissions.length > 0
				? allSpaces.filter(Boolean)
				: allSpaces.filter(
						(s) =>
							s &&
							(s.visibility === "PUBLIC" || s.isMember) &&
							s.postPolicy !== "RESTRICTED",
					);

		const initialSpaceId = space
			? availableSpaces.find((s) => s.slug === space)?.id
			: undefined;

		return { initialSpaceId, availableSpaces, rules: data?.club.rules };
	},
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });

	const { club, session } = useRouteContext({ from: "__root__" });

	const { initialSpaceId, availableSpaces, rules } = useLoaderData({
		from: Route.id,
	});

	const [mutate] = useMutation<postsCreateMutation>(graphql`
	   mutation postsCreateMutation($payload: CreatePostInput!) {
			createPost(payload: $payload) {
	        postEdge {
					node {
		          id
						slug
						club {
							id
							scheduledContent {
                  pageInfo {
                    totalCount
                  }
                }
						}
				    space {
							id
							slug
						}
					}
				}
			}
		}
	`);

	const { track } = useAnalytics();
	const [isPending, setIsPending] = useState(false);

	const isModerator =
		club.permissions?.some((p) => p.slug === "MODERATOR") || false;
	const isAdmin =
		club.permissions?.some((p) => p.slug === "CLUB_ADMIN") || false;

	const onSubmit = (value: PostFormSchemaValues) => {
		setIsPending(true);
		return mutate({
			variables: {
				payload: {
					title: value.title,
					content: JSON.stringify(value.content),
					space: value.space,
					club: club.slug,
					cover: value.cover ? { coverContent: value.cover.id } : null,
					attachments: value.attachments.map((it) => ({ file: it.id })),

					...(isAdmin
						? {
								notifyMembers: value.notifyUsers,
							}
						: {}),
					...(isModerator || isAdmin
						? {
								scheduledPublishDate: value.schedule
									? value.scheduledDate
									: null,
								commentPolicy: value.commentPolicy,
								pinned: value.pinned,
								pinnedUniversal: value.pinnedUniversal,
								hideMetadata: value.hideMetadata,
							}
						: {}),
				},
			},
			onCompleted: (res) => {
				track(AnalyticsEvents.POST_CREATED, {
					space_id: res.createPost.postEdge.node.space?.id || "",
					has_cover: !!value.cover,
					has_attachments: value.attachments.length > 0,
					is_scheduled: !!value.schedule,
				});

				toast.success("Post criado com sucesso!");

				navigate({
					to: "/comunidade/s/$space/posts/$post",
					params: {
						space: res.createPost.postEdge.node.space?.slug!,
						post: res.createPost.postEdge.node.slug,
					},
				});
			},
			onError: () => {
				setIsPending(false);
				toast.error(
					"Ocorreu um erro ao criar o post. Tente novamente mais tarde.",
				);
			},
			updater: (store, res) => {
				if (res?.createPost.postEdge.node.space) {
					const item = store.get(res?.createPost.postEdge.node.space?.id);

					if (item) {
						const bestConnection = ConnectionHandler.getConnection(
							item,
							"spaceFeedFragment__feed",
							{ ignoreFixed: false, orderBy: "BEST" },
						);

						const newestConnection = ConnectionHandler.getConnection(
							item,
							"spaceFeedFragment__feed",
							{ ignoreFixed: false, orderBy: "NEWEST" },
						);

						const oldestConnection = ConnectionHandler.getConnection(
							item,
							"spaceFeedFragment__feed",
							{ ignoreFixed: false, orderBy: "OLDEST" },
						);

						bestConnection?.invalidateRecord();
						newestConnection?.invalidateRecord();
						oldestConnection?.invalidateRecord();
					}
				}

				if (res?.createPost.postEdge.node.club) {
					const clubItem = store.get(res?.createPost.postEdge.node.club.id);

					if (clubItem) {
						const bestClubConnection = ConnectionHandler.getConnection(
							clubItem,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "BEST" },
						);

						const newestClubConnection = ConnectionHandler.getConnection(
							clubItem,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "NEWEST" },
						);

						const oldestClubConnection = ConnectionHandler.getConnection(
							clubItem,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "OLDEST" },
						);

						const scheduledClubConnection = ConnectionHandler.getConnection(
							clubItem,
							"posts_scheduledContent",
						);

						bestClubConnection?.invalidateRecord();
						newestClubConnection?.invalidateRecord();
						oldestClubConnection?.invalidateRecord();
						scheduledClubConnection?.invalidateRecord();
					}
				}
			},
		});
	};

	const rulesEditor = useEditor({
		editable: false,
		content: rules?.description,
		session,
		club: club.slug,
		mimeTypes: ["*"],
		size: "sm",
		features: {
			colors: { enabled: true },
			button: { enabled: true },
			mention: { enabled: true },
			files: { enabled: false },
		},
	});

	return (
		<div className="w-full flex flex-row items-start justify-center gap-6 p-6">
			<Card className="p-6 w-full max-md:px-4 max-w-[700px]">
				<div className="flex flex-col gap-6">
					<Heading>Criar Post</Heading>

					<PostForm
						onSubmit={onSubmit}
						spaces={availableSpaces}
						initialSpace={initialSpaceId}
						isPending={isPending}
					/>
				</div>
			</Card>

			{rules && (
				<Card className="hidden h-[70dvh] md:flex p-6 flex-col gap-6 overflow-y-auto w-[340px] min-w-[340px] max-w-[340px]">
					<Heading level={3}>Regras da Comunidade</Heading>

					<EditorContent editor={rulesEditor} />
				</Card>
			)}
		</div>
	);
}
